import { ActivatedRoute } from '@angular/router';
import { computed, Injectable, inject, signal } from '@angular/core';

import { Card, PaymentPlansResponse } from '../checkout-model';
import { SessionVariable } from '../../common/enum/session-variable';
import { LocalStorageService } from '../../common/services/local-storage.service';
import {
  PaymentPlanId,
  OrderDetails,
  PaymentDetails,
  PaymentPlanDisabledReason,
} from '../../common/models/order.model';
import { getDueTodayAmount, isPayLaterPlan } from '../../common/utils/payment-plan.util';

@Injectable({
  providedIn: 'root',
})
export class CheckoutSignalsService {
  private readonly localStorageService = inject(LocalStorageService);

  customerInfo = signal<any>(null);
  isLoading = signal<boolean>(true);
  orderId = signal<string | null>(null);
  addedCreditCard = signal<Card[] | null>(null);
  // Typed non-null for consumers; actually null until the order/payment info loads.
  orderDetails = signal<OrderDetails>(null!);
  paymentDetails = signal<PaymentDetails>(null!);
  selectedInstallmentIds = signal<number[]>([]);
  paymentPlanId = signal<PaymentPlanId | null>(null);
  selectedPaymentMethod = signal<string | null>(null);
  paymentOptions = signal<PaymentPlansResponse[] | null>(null);
  isPayLaterSelected = signal<boolean>(this.getStoredIsPayLaterSelected());

  selectedPlan = computed(
    () => this.paymentOptions()?.find((plan) => plan.id === this.paymentPlanId()) ?? null
  );

  hasMultiplePlans = computed(() => (this.paymentOptions()?.length ?? 0) > 1);

  dueTodayAmount = computed(() => getDueTodayAmount(this.selectedPlan()));

  isSelectedPlanNotEligible = computed(
    () => this.selectedPlan()?.disabledReason === PaymentPlanDisabledReason.NOT_ELIGIBLE
  );

  isSelectedPlanLimitExceeded = computed(
    () => this.selectedPlan()?.disabledReason === PaymentPlanDisabledReason.LIMIT_EXCEEDED
  );

  isSelectedPlanDisabled = computed(() => !!this.selectedPlan()?.disabledReason);

  setIsLoading(isLoading: boolean) {
    this.isLoading.set(isLoading);
  }

  setSelectedPaymentMethod(selectedPaymentMethod: string) {
    this.selectedPaymentMethod.set(selectedPaymentMethod);
  }

  setIsPayLaterSelected(isPayLaterSelected: boolean) {
    this.isPayLaterSelected.set(isPayLaterSelected);
    this.localStorageService.setItem(SessionVariable.IS_PAY_LATER_SELECTED, isPayLaterSelected);
  }

  setOrderId(orderId: string) {
    if (!orderId) {
      return;
    }

    this.orderId.set(orderId);
    this.localStorageService.setItem(SessionVariable.ORDER_ID, orderId);
  }

  resolveOrderId(route?: ActivatedRoute | null): string | null {
    return route
      ? this.getOrderIdFromRoute(route)
      : this.orderId() || this.localStorageService.getItem<string>(SessionVariable.ORDER_ID);
  }

  syncOrderIdFromRoute(route: ActivatedRoute): string | null {
    const orderId = this.resolveOrderId(route);

    if (orderId) {
      this.setOrderId(orderId);
    }

    return orderId;
  }

  private getOrderIdFromRoute(route: ActivatedRoute): string | null {
    let current: ActivatedRoute | null = route;

    while (current) {
      const orderId = current.snapshot.paramMap.get('orderId');

      if (orderId) {
        return orderId;
      }

      current = current.parent;
    }

    return null;
  }

  setPaymentPlanId(paymentPlanId: PaymentPlanId) {
    this.paymentPlanId.set(paymentPlanId);
    this.localStorageService.setItem(SessionVariable.PAYMENT_PLAN_ID, paymentPlanId);
    this.setIsPayLaterSelected(isPayLaterPlan(paymentPlanId));
  }

  setOrderDetails(orderDetails: OrderDetails) {
    this.orderDetails.set(orderDetails);
  }

  setPaymentDetails(paymentDetails: PaymentDetails) {
    this.paymentDetails.set(paymentDetails);
  }

  setCustomerInfo(customerInfo: any) {
    this.customerInfo.set(customerInfo);
  }

  setPaymentOptions(paymentOptions: PaymentPlansResponse[]) {
    this.paymentOptions.set(paymentOptions);
  }

  setSelectedInstallmentIds(installmentIds: number[]) {
    this.selectedInstallmentIds.set(installmentIds);
  }

  toggleInstallmentSelection(installmentId: number) {
    const current = this.selectedInstallmentIds();
    const isSelected = current.includes(installmentId);

    this.selectedInstallmentIds.set(
      isSelected ? current.filter((id) => id !== installmentId) : [...current, installmentId]
    );
  }

  setAddedCreditCard(addedCreditCard: Card[]) {
    this.addedCreditCard.set(addedCreditCard);
  }

  clearAll() {
    this.orderDetails.set(null!);
    this.customerInfo.set(null);
    this.paymentPlanId.set(null);
    this.paymentDetails.set(null!);
    this.paymentOptions.set(null);
    this.addedCreditCard.set(null);
    this.isPayLaterSelected.set(false);
    this.selectedInstallmentIds.set([]);
  }

  private getStoredIsPayLaterSelected(): boolean {
    return (
      this.localStorageService.getItem<boolean>(SessionVariable.IS_PAY_LATER_SELECTED) ?? false
    );
  }
}
